import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Milk, Droplets, Sprout, HandHeart } from 'lucide-react';

// ─── Lazy Video Component ──────────────────────────────────────────────────────
// Loads and plays a video only when it enters the viewport (IntersectionObserver).
// Pauses and unloads src when scrolled away to free memory.
// Degrades gracefully: if IntersectionObserver is unsupported, falls back to eager load.
const LazyVideo = React.memo(({ src, className, title, ariaLabel, overlayLayout = false }) => {
  const videoRef = useRef(null);
  const observerRef = useRef(null);

  const startVideo = useCallback((video) => {
    if (!video || video.dataset.loaded === 'true') return;
    video.dataset.loaded = 'true';
    // Set src on first intersection — browser only starts download here
    const source = video.querySelector('source');
    if (source && !source.src) {
      source.src = source.dataset.src;
    }
    video.load();
    video.play().catch(() => {
      // Autoplay blocked (e.g. browser policy) — silently ignore, video stays paused
    });
  }, []);

  const stopVideo = useCallback((video) => {
    if (!video) return;
    video.pause();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Fallback: browsers without IntersectionObserver get the video immediately
    if (typeof IntersectionObserver === 'undefined') {
      startVideo(video);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startVideo(video);
          } else {
            stopVideo(video);
          }
        });
      },
      {
        rootMargin: '100px', // start loading 100px before entering view
        threshold: 0.1,
      }
    );

    observerRef.current.observe(video);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      stopVideo(video);
    };
  }, [src, startVideo, stopVideo]);

  return (
    <video
      ref={videoRef}
      autoPlay
      loop
      muted
      playsInline
      preload="none"     // ← critical: browser downloads nothing until IntersectionObserver fires
      role="img"
      title={title}
      aria-label={ariaLabel}
      className={className}
    >
      {/* data-src instead of src: prevents browser from fetching on mount */}
      <source data-src={src} type="video/mp4" />
    </video>
  );
});

LazyVideo.displayName = 'LazyVideo';

// ─── Features Component ────────────────────────────────────────────────────────
export const Features = React.memo(({ t }) => {
  // useMemo: features array is stable across re-renders; icons are module refs, not new objects
  const features = useMemo(() => [
    {
      icon: Milk,
      title: t.feature1Title,
      description: t.feature1Desc,
      video: '/COMPRESSEDprocessing.mp4',
      layout: 'overlay',
      position: 'items-end justify-end',
      gradient: 'from-brand-navy to-brand-navy/80',
    },
    {
      icon: Droplets,
      title: t.feature2Title,
      description: t.feature2Desc,
      video: '/milkporCOMPRESSED.mp4',
      layout: 'overlay',
      position: 'items-end justify-start',
      gradient: 'from-brand-pink to-brand-pink/80',
    },
    {
      icon: Sprout,
      title: t.feature3Title,
      description: t.feature3Desc,
      video: '/cowgrazingCOMPRESSED.mp4',
      layout: 'overlay',
      position: 'items-start justify-end',
      gradient: 'from-[#1DA851] to-whatsapp',
    },
    {
      icon: HandHeart,
      title: t.feature4Title,
      description: t.feature4Desc,
      video: '/desimilkextractionCOMPRESSED.mp4',
      layout: 'overlay',
      position: 'items-start justify-start',
      gradient: 'from-brand-pink to-brand-pink/80',
    },
  ], [
    t.feature1Title, t.feature1Desc,
    t.feature2Title, t.feature2Desc,
    t.feature3Title, t.feature3Desc,
    t.feature4Title, t.feature4Desc,
  ]);

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold font-serif text-brand-navy mb-4">
            {t.featuresTitle}
          </h2>
          <p className="text-xl text-gray-600">
            {t.featuresSubtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="group overflow-hidden border-2 hover:border-brand-pink/30 transition-all duration-300 hover:shadow-2xl"
            >
              <CardContent className="p-0 h-full">
                <div className={`relative h-full min-h-[400px] flex ${feature.position || 'items-end justify-start'} p-4 sm:p-6`}>
                  {/* Background Video — lazy loaded */}
                  <div className="absolute inset-0 z-0 overflow-hidden">
                    <LazyVideo
                      src={feature.video}
                      title={`${feature.title} — RajDarshan Tandon Dairy Raipur`}
                      ariaLabel={`Video showing ${feature.title}: ${feature.description}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:bg-black/40 transition-colors duration-300" />
                  </div>

                  {/* Content Box */}
                  <div className="relative z-10 bg-white/95 backdrop-blur-md p-5 sm:p-6 rounded-2xl shadow-xl w-[90%] sm:w-[80%] max-w-sm transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500 border border-white/20">
                    <div className={`inline-flex w-10 h-10 sm:w-12 sm:h-12 items-center justify-center rounded-full bg-gradient-to-br ${feature.gradient} mb-3 sm:mb-4 shadow-lg`}>
                      <feature.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ── Alert pill: Fake Milk Warning ────────────── */}
      <Link
        to="/pure-cow-buffalo-milk"
        className="pill-float-2 inline-flex items-center gap-2 bg-red-600 text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-xl border-2 border-white/40 cursor-pointer no-underline transition-all duration-200"
      >
        <span>⚠️</span> ALERT FOR YOU <span className="ml-1 opacity-80">→</span>
      </Link>
    </section>
  );
});

Features.displayName = 'Features';
